'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useActionState } from 'react';
import { loginAction, type LoginActionState } from '../../src/server/actions/login.action';
import { showToast } from '../../src/components/ui-kontora/use-toast';

const initialState: LoginActionState = {};
const subscribeToHydration = () => () => undefined;

export default function LoginPage() {
  const [state, formAction, isPending] = useActionState(loginAction, initialState);
  const lastHandledState = useRef<LoginActionState | null>(null);
  const ready = useSyncExternalStore(
    subscribeToHydration,
    () => true,
    () => false,
  );

  useEffect(() => {
    if (lastHandledState.current === state) return;
    lastHandledState.current = state;
    if (state.error) {
      showToast({
        variant: 'error',
        title: 'Anmeldung fehlgeschlagen',
        description: state.error,
      });
    } else if (state.success) {
      showToast({
        variant: 'success',
        title: 'Erfolgreich angemeldet',
        description:
          state.redirectTo === '/einstellungen/2fa'
            ? 'Bitte richten Sie zuerst Ihren verpflichtenden zweiten Faktor ein.'
            : 'Sie werden zum Dashboard weitergeleitet.',
      });
      if (state.redirectTo) {
        window.location.assign(state.redirectTo);
      }
    }
  }, [state]);

  return (
    <main className="grid min-h-screen lg:grid-cols-2 bg-[var(--k-color-bg-app)]">
      <section className="hidden flex-col justify-between bg-[linear-gradient(135deg,#0f3252_0%,#16476e_55%,#1e5b8c_100%)] p-12 text-white lg:flex">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-[var(--k-radius-md)] bg-white/15 text-lg font-bold backdrop-blur">
              K
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                Kontora
              </p>
              <p className="text-lg font-semibold">Buchhaltung klar geführt.</p>
            </div>
          </div>
        </div>
        <div className="max-w-md space-y-4">
          <h2 className="text-3xl font-semibold leading-tight">
            Belege, Buchungen und Berichte – an einem Ort, mit klaren Rollen.
          </h2>
          <p className="text-base text-white/85">
            Diese Anmeldung führt Sie in die Plattform. Ihre Rolle bestimmt, welche Bereiche
            sichtbar sind: Arbeit, Buchhaltung, Steuerberater, Administration oder System.
          </p>
          <ul className="space-y-2 text-sm text-white/80">
            <li className="flex items-start gap-2">
              <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 rounded-full bg-white/80" />
              Echtzeit-Dashboard mit OPOS, Cashflow und Risiken
            </li>
            <li className="flex items-start gap-2">
              <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 rounded-full bg-white/80" />
              Steuer- und UStVA-Logik aus der Datenbank
            </li>
            <li className="flex items-start gap-2">
              <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 rounded-full bg-white/80" />
              Zentrales Error-Log für Backend und Frontend
            </li>
          </ul>
        </div>
        <p className="text-xs text-white/85">© Kontora · Sicher angemeldet bleiben</p>
      </section>

      <section className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <h1 className="k-sr-only">Anmeldung Kontora</h1>
          <div className="mb-8 text-center lg:hidden">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[var(--k-radius-md)] bg-[var(--k-color-accent)] text-lg font-bold text-[var(--k-color-text-on-accent)]">
              K
            </div>
            <h1 className="text-2xl font-semibold text-[var(--k-color-text-primary)]">Kontora</h1>
            <p className="text-sm text-[var(--k-color-text-muted)]">Buchhaltung klar geführt.</p>
          </div>

          <div className="rounded-[var(--k-radius-lg)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] p-8 shadow-[var(--k-shadow-md)]">
            <h2 className="text-2xl font-semibold text-[var(--k-color-text-primary)]">Anmelden</h2>
            <p className="mt-1 text-sm text-[var(--k-color-text-secondary)]">
              Mit Ihrer E-Mail-Adresse und Ihrem Passwort.
            </p>

            {state.error && (
              <div
                role="alert"
                className="mt-6 rounded-[var(--k-radius-md)] border border-[var(--k-color-danger)]/30 bg-[var(--k-color-danger-soft)] px-3 py-2 text-sm font-medium text-[var(--k-color-danger-strong)]"
              >
                {state.error}
              </div>
            )}

            <form action={formAction} className="mt-6 space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="mb-1 block text-sm font-semibold text-[var(--k-color-text-primary)]"
                >
                  E-Mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-3.5 py-2.5 text-sm text-[var(--k-color-text-primary)] placeholder:text-[var(--k-color-text-muted)] focus:border-[var(--k-color-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--k-color-accent)]/20"
                  placeholder="name@unternehmen.de"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1 block text-sm font-semibold text-[var(--k-color-text-primary)]"
                >
                  Passwort
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="w-full rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-3.5 py-2.5 text-sm text-[var(--k-color-text-primary)] placeholder:text-[var(--k-color-text-muted)] focus:border-[var(--k-color-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--k-color-accent)]/20"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label
                  htmlFor="twoFactorCode"
                  className="mb-1 block text-sm font-semibold text-[var(--k-color-text-primary)]"
                >
                  Authentifizierungscode
                </label>
                <input
                  id="twoFactorCode"
                  name="twoFactorCode"
                  type="text"
                  autoComplete="one-time-code"
                  maxLength={32}
                  aria-describedby="two-factor-help"
                  className="w-full rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-3.5 py-2.5 text-sm text-[var(--k-color-text-primary)] focus:border-[var(--k-color-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--k-color-accent)]/20"
                />
                <p
                  id="two-factor-help"
                  className="mt-1 text-xs text-[var(--k-color-text-secondary)]"
                >
                  Falls eingerichtet: Code aus Ihrer Authenticator-App oder einmaliger Backup-Code.
                </p>
              </div>

              <button
                type="submit"
                disabled={!ready || isPending}
                className="inline-flex w-full items-center justify-center rounded-[var(--k-radius-md)] bg-[var(--k-color-accent)] px-4 py-2.5 text-sm font-semibold text-[var(--k-color-text-on-accent)] transition-shadow hover:bg-[var(--k-color-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--k-color-surface-muted)] disabled:text-[var(--k-color-text-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--k-color-surface)]"
              >
                {isPending ? 'Anmelden…' : 'Anmelden'}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-[var(--k-color-text-muted)]">
            Bei Problemen wenden Sie sich an Ihren Administrator.
          </p>
        </div>
      </section>
    </main>
  );
}
